import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";
import SelectBox from "@/js/common/ui/input/SelectBox";
import Button from "@/js/common/ui/Button";
import { exportToXlsx } from "@/js/common/ExcelExport";
import { fldInfsProps } from "@/js/common/ui/GridTable";
import { useRecoilValue } from "recoil";
import * as Atom from "@/js/common/Atom";

/**
 * StatsOvrlMbsdsMonUpOgnzGrnamtInq.tsx
 * ------------------------------------------------------------
 */

const StatsOvrlMbsdsMonUpOgnzGrnamtInq = () => {
    const userInfo = useRecoilValue(Atom.userInfo);

    const [acctYear, setAcctYear] = React.useState<string>(Util.getNowAcntgYear());
    const [ognzCd, setOgnzCd] = React.useState<string>(
        userInfo.ognzCd.includes("09") || userInfo.ognzCd.includes("10") ? "00_000" : userInfo.ognzCd
    );

    const [dataArr, setDataArr] = React.useState<any[]>([]);

    const [totalGrnamt, setTotalGrnamt] = React.useState<string>("0");
    const [totalEmpGrnamt, setTotalEmpGrnamt] = React.useState<string>("0");
    const [totalSum, setTotalSum] = React.useState<string>("0");

    React.useEffect(() => {
        if (Util.isEmpty(acctYear) || Util.isEmpty(ognzCd)) {
            setDataArr([]);
            setTotalGrnamt("0");
            setTotalEmpGrnamt("0");
            setTotalSum("0");
            return;
        }

        AppContext.showSpinner();

        Axios.dataAccess({
            url: "system/stats/select-stats-ovrl-aprv-ognz-month.do",
            methodType: "post",
            paramType: "json",
            paramData: {
                acntgYear: acctYear,
                ognzCd: ognzCd,
            },
            onSuccessFn: (res: any) => {
                setDataArr(res.result.list);

                setTotalGrnamt(res.result.listSum.totGrnamtAmt);
                setTotalEmpGrnamt(res.result.listSum.totPostalGrnamtAmt);
                setTotalSum(res.result.listSum.totSumAmt);

                AppContext.hideSpinner();
            },
        });
    }, [acctYear, ognzCd]);

    const fldInfs: fldInfsProps[] = [
        { type: "text",  fldNm: "acntgMonth",         width: 80,  headAlign: "center", dataAlign: "center", label: "월" },
        { type: "comma", fldNm: "totGrnamtAmt",       width: 150, headAlign: "center", dataAlign: "right",  label: "교부금" },
        { type: "comma", fldNm: "totPostalGrnamtAmt", width: 170, headAlign: "center", dataAlign: "right",  label: "우정실무원 교부금" },
        { type: "comma", fldNm: "totSumAmt",          width: 150, headAlign: "center", dataAlign: "right",  label: "총계" },
    ];

    // 엑셀 다운로드
    function downloadExcel() {
        if (dataArr.length === 0) {
            AppContext.openAlert({
                title: "데이터 확인",
                msg: "엑셀로 다운로드할 데이터가 없습니다.",
                icon: "warning",
            });
            return;
        }

        const totalRow = {
            acntgYear: "",
            acntgMonth: "총 합계",
            totGrnamtAmt: Number(Util.uncomma(totalGrnamt || "0")),
            totPostalGrnamtAmt: Number(Util.uncomma(totalEmpGrnamt || "0")),
            totSumAmt: Number(Util.uncomma(totalSum || "0")),
            excelRowStyle : {
                fillColor: "FFEF99",
                fontColor: "000000",
                bold: true,
            },
        };

        const excelRows = [
            ...dataArr,
            totalRow,
        ];

        exportToXlsx({
            fileName: "지방본부별 교부금 통계",
            title: `${acctYear}년 지방본부별 교부금 통계`,
            subTitleLeft: `${acctYear}회계년도`,
            subTitleRight: "(금액단위:원)",
            columns: fldInfs,
            rows: excelRows,
        });
    }

    return (
        <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
            {/* 타이틀 */}
            <div className="mt5">
                <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">
                    지방본부별 교부금 통계
                </span>
                <div className="bdt3 bdc007bff pb10" />
            </div>

            {/* 검색 조건 */}
            <div
                className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA"
                style={{ position: "relative", zIndex: 50 }}
            >
                <div className="dpFlx aiC gap14 mb10">
                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">지방 본부</span>
                        <SelectBox
                            value={ognzCd}
                            width={140}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#FFF"
                            codeArray={Util.getOvrlOgnzList()}
                            placeholder="전체"
                            paddingRight={8}
                            onChangeFunc={(data: string) => setOgnzCd(data)}
                        />
                    </div>

                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">회계연도</span>
                        <SelectBox
                            value={acctYear}
                            width={120}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#fff"
                            codeArray={Util.getAcntgYearArray()}
                            placeholder="선택"
                            paddingRight={8}
                            onChangeFunc={(data: string) => setAcctYear(data)}
                        />
                    </div>

                    {/* 버튼 */}
                    <div className="dpFlx aiC gap12 mlA">
                        <Button
                            width={150}
                            height={40}
                            fontFamily="fm-b"
                            fontSize={16}
                            letterSpacing={-1}
                            variant="styExcelBtn"
                            borderRadius={6}
                            buttonClass="dpFlx aiC jcC"
                            buttonName="Excel 다운로드"
                            onClickFunc={downloadExcel}
                        />
                    </div>
                </div>
            </div>

            {/* 테이블 */}
            <div
                className="w100p"
                style={{
                    maxHeight: 545,
                    overflowY: "auto",
                    border: "1px solid #DDDDDD",
                    borderRadius: 8,
                }}
            >
                <table className="w100p tlF" style={{ borderCollapse: "collapse" }}>
                    <colgroup>
                        <col style={{ width: 180 }} />
                        <col style={{ width: 320 }} />
                        <col style={{ width: 320 }} />
                        <col style={{ width: 320 }} />
                    </colgroup>

                    <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                        <tr>
                            {["월", "교부금", "우정실무원 교부금", "합계"].map((label) => (
                                <th
                                    key={label}
                                    className="bd1 bdcDDDDDD taC fm-b fs14 fc333333"
                                    style={{ padding: "10px 12px", background: "#EFF8FB" }}
                                >
                                    {label}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {dataArr.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="bd1 bdcDDDDDD p16 taC fm-c fs14 fc666666">
                                    조회 결과가 없습니다.
                                </td>
                            </tr>
                        ) : (
                            dataArr.map((r: any, idx: number) => (
                                <tr key={idx}>
                                    <td className="bd1 bdcDDDDDD p10 taC fm-c fs14">
                                        {Util.comma(String(r.acntgMonth))}
                                    </td>
                                    <td className="bd1 bdcDDDDDD p10 taR fm-c fs14 fw700 bdcDDDDDD">
                                        {Util.comma(String(r.totGrnamtAmt))}
                                    </td>
                                    <td className="bd1 bdcDDDDDD p10 taR fm-c fs14 fw700 bdcDDDDDD">
                                        {Util.comma(String(r.totPostalGrnamtAmt))}
                                    </td>
                                    <td className="bd1 bdcDDDDDD p10 taR fm-c fs14 fw700 bdcDDDDDD">
                                        {Util.comma(String(r.totSumAmt))}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>

                    <tfoot>
                        <tr style={{ position: "sticky", bottom: 0, background: "#E1E1E1" }}>
                            <td className="bd1 bdcDDDDDD p10 taC fm-c fs14 fw700">총 합계</td>
                            <td className="bd1 bdcDDDDDD p10 taR fm-c fs14 fw700 fc007bff">
                                {Util.comma(totalGrnamt)}
                            </td>
                            <td className="bd1 bdcDDDDDD p10 taR fm-c fs14 fw700 fc007bff">
                                {Util.comma(totalEmpGrnamt)}
                            </td>
                            <td className="bd1 bdcDDDDDD p10 taR fm-c fs14 fw700 fc007bff">
                                {Util.comma(totalSum)}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

export default StatsOvrlMbsdsMonUpOgnzGrnamtInq;